import React, { useState, useCallback, useEffect, useRef } from "react";

// recoil
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, routeInfo } from "recoilState/GlobalState";

// utils
import { useFormik } from "formik";
import { Buttons, HeaderButton } from "components";
import { FilterAltOffRounded, Cached, UploadFile, VisibilityOutlined, Add, DeleteOutlined } from "@mui/icons-material";
import Filters from "components/Filters";
import { loteVouchers as titles } from "consts/titulos-de-tablas";
import { SFLoteVoucherGet } from "services/services-mia/SFHotel";

// styles
import s from "styles/pages/Pages.module.scss";
import { ACTIONS } from "consts/Actions";
import { Routes } from "consts/Routes-App";
import Modal from "components/UI/Modal/Modal";
import { DetailModal, VoucherCSVModal, InsertModal, LoteDeleteModal } from "./Actions";
import XGridServer from "components/UI/Grid/XGridServer";
import { snackbarData } from "recoilState/GlobalState";

const formatDNI = (dni) => {
  return dni.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const baseDate = dateStr.slice(0, 10);
  const parts = baseDate.split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  return baseDate.replace(/-/g, "/");
};

export default function Vouchers() {
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
  const [resetTable, setResetTable] = useState(false);
  const [forceUpload, setForceUpload] = useState(false);
  const rowToDeleteRef = useRef({});
  const skipResetRef = useRef(false);
  const credentials = useRecoilValue(userCredentials);
  const setSnackbarInfo = useSetRecoilState(snackbarData);
  const setRoute = useSetRecoilState(routeInfo);

  useEffect(() => {
    setRoute(Routes.vouchers);
  }, [setRoute]);

  const formik = useFormik({
    initialValues: {
      voucher: "",
      documento: "",
      fechaDesde: "",
    },
    onSubmit: () => {
      setResetTable((value) => !value);
    },
  });

  const getLoteVouchers = useCallback(async (page, pageSize) => {
    if (!skipResetRef.current) {
      setRowSelected({ status: false, row: {} });
    }
    skipResetRef.current = false;
    const { documento, fechaDesde } = formik.values;

    try {
      const { result, total } = await SFLoteVoucherGet({
        SFVoucherTipo: 'A',
        page: page + 1,
        pageSize,
        usuarioNroDeDocumentoX: documento || null,
        SFVoucherFecha: fechaDesde || null,
      }, credentials);

      const mappedRows = (result || []).map((item) => {
        return {
          id: item.SFLoteVoucherId,
          SFLoteVoucherId: item.SFLoteVoucherId,
          SFLoteVoucherMsuId: item.SFLoteVoucherMsuId,
          SFLoteVoucherFecha: item.SFLoteVoucherFecha,
          SFLoteVoucherTipo: item.SFLoteVoucherTipo,
          SFLoteVoucherFechaDesde: item.SFLoteVoucherDesde,
          SFLoteVoucherFechaHasta: item.SFLoteVoucherHasta,
          merchantServicioUsuarioNombre: item.merchantServicioUsuarioNombre,
          merchantServicioUsuarioApellido: item.merchantServicioUsuarioApellido,
          merchantServicioUsuarioDniX: item.merchantServicioUsuarioDniX,
          cantidadVouchers: item.cantidadVouchers,
          cantidadUsados: item.cantidadUsados,  
          numeroDesde: item.numeroDesde,
          numeroHasta: item.numeroHasta,

          [titles[0].field]: item.merchantServicioUsuarioApellido,
          [titles[1].field]: item.merchantServicioUsuarioNombre,
          [titles[2].field]: item.tipoDeDocumentoCod || "DNI",
          [titles[3].field]: item.merchantServicioUsuarioDniX ? formatDNI(item.merchantServicioUsuarioDniX) : "",
          [titles[4].field]: item.SFLoteVoucherFecha ? formatDate(item.SFLoteVoucherFecha) : "",
          [titles[5].field]: item.SFLoteVoucherDesde ? formatDate(item.SFLoteVoucherDesde) : "",
          [titles[6].field]: item.SFLoteVoucherHasta ? formatDate(item.SFLoteVoucherHasta) : "",
          [titles[7].field]: item.cantidadVouchers,
          [titles[8].field]: item.cantidadVouchers - item.cantidadUsados,
          [titles[9].field]: item.cantidadUsados,
          [titles[10].field]: item.numeroDesde,
          [titles[11].field]: item.numeroHasta,
        };
      });

      return {
        rows: mappedRows,
        rowCount: total || mappedRows.length,
      };

    } catch (error) {
      console.error("Error al obtener los vouchers:", error);
      setSnackbarInfo({
        open: true,
        severity: "error",
        message: error?.message || "Error al obtener los vouchers",
      });

      return { rows: [], rowCount: 0 };
    }
  }, [credentials, setSnackbarInfo, formik.values]); // eslint-disable-line react-hooks/exhaustive-deps

  const baseHandleChange = formik.handleChange;
  formik.handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "documento") {
      const numericValue = value.replace(/\D/g, "");
      e.target.value = numericValue.slice(0, 8); // Máximo 8 dígitos
    } else if (name === "voucher") {
      e.target.value = value.replace(/\D/g, "");
    }
    baseHandleChange(e);
  };

  const resetFilters = () => {
    formik.setValues({ voucher: "", documento: "", fechaDesde: "" });
    setResetTable((value) => !value);
  };

  const handleClick = ({ procede, title }) => {
    setModalDetails({ title, procede, isOpen: true });
  };

  const closeModal = () => {
    setModalDetails({ isOpen: false });
  };

  return (
    <main className={s.main}>
      <Modal onClose={closeModal} open={modalDetails.isOpen} title={modalDetails.title}>
        {modalDetails.procede === ACTIONS.BATCH && (
          <VoucherCSVModal
            updateList={() => setForceUpload((v) => !v)}
            closeModal={closeModal}
          />
        )}
        {modalDetails.procede === ACTIONS.DETAIL && (
          <DetailModal
            updateList={() => { skipResetRef.current = true; setForceUpload((v) => !v); }}
            closeModal={closeModal}
            loteVoucherId={rowSelected.row.SFLoteVoucherId}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            updateList={() => setForceUpload((v) => !v)}
            closeModal={closeModal}
          />
        )}
        {modalDetails.procede === ACTIONS.DELETE && (
          <LoteDeleteModal
            updateList={() => setForceUpload((v) => !v)}
            closeModal={closeModal}
            row={rowToDeleteRef.current}
          />
        )}
      </Modal>
      <div className={s["filters-actions"]}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Filters
            formik={formik}
            inputsRender={[
              { type: "text", name: "documento", label: "Documento", disabled: false },
            ]}
          />
          <HeaderButton text={"Limpiar Filtros"} onClick={resetFilters}>
            <FilterAltOffRounded />
          </HeaderButton>
        </div>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={() => setForceUpload((v) => !v)}
          >
            <Cached />
          </HeaderButton>

          <HeaderButton
            text={"Detalle de Vouchers"}
            disabled={!rowSelected.row.id}
            handleClick={handleClick}
            data={{
              title: "Detalle de Vouchers",
              procede: ACTIONS.DETAIL
            }}
          >
            <VisibilityOutlined />
          </HeaderButton>

          <HeaderButton
            text={"Agregar Vouchers"}
            handleClick={handleClick}
            data={{
              title: "Agregar Vouchers",
              procede: ACTIONS.INSERT
            }}
            disabled={rowSelected.row.id}
          >
            <Add />
          </HeaderButton>

          <HeaderButton
            text={"Eliminar Lote"}
            onClick={() => {
              rowToDeleteRef.current = { ...rowSelected.row };
              setModalDetails({ title: "Eliminar Lote de Vouchers", procede: ACTIONS.DELETE, isOpen: true });
            }}
            disabled={
              !rowSelected.row.id ||
              (rowSelected.row.cantidadVouchers - rowSelected.row.cantidadUsados === 0 && rowSelected.row.cantidadUsados > 0) ||
              (rowSelected.row.SFLoteVoucherFechaHasta?.slice(0, 10) < new Date().toISOString().slice(0, 10))
            }
          >
            <DeleteOutlined />
          </HeaderButton>

          <HeaderButton
            text={"Importar Vouchers CSV"}
            handleClick={handleClick}
            data={{
              title: "Importar Vouchers",
              procede: ACTIONS.BATCH
            }}
            disabled={rowSelected.row.id}
          >
            <UploadFile />
          </HeaderButton>
        </Buttons>
      </div>
      <div className={s.container}>
        <XGridServer

          titles={titles}
          updateList={getLoteVouchers}
          forceReset={resetTable}
          forceUpload={forceUpload}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>
    </main>
  );
}
